import { BLOCKS } from "@contentful/rich-text-types";
import { colours, device, getColours, richTextHelper } from "@helpers";
import type { IModuleHeaderFields } from "@types-contentful";
import { usePathname } from "next/navigation";
import QRCode from "qrcode.react";
import React, { useState, useEffect } from "react";
import styled from "styled-components";

const Container = styled.div<{
	resetModuleMarginBottom: "No" | "Yes" | undefined;
	isDomesticViolencePage: boolean;
}>`
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
  ${({ isDomesticViolencePage }) =>
		isDomesticViolencePage && "margin-top: 90px;"};
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;
const CenterContainer = styled.div`
  text-align: center;
`;

const TextContainer = styled.div<{
	backgroundColor:
		| "Midnight Blue"
		| "White"
		| "Peach"
		| "Pale Peach"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
}>`
  border-radius: 60px 0px 0px 0px;
  box-shadow: -5px -5px 30px 0 rgba(18, 18, 82, 0.2);
  background-color: ${({ backgroundColor }) => getColours(backgroundColor)};
  padding: 42px 39px;

  @media (min-width: ${device.tablet}) and (max-width: ${device.laptopM}) {
    padding: 47px 64px;
  }

  @media (min-width: ${device.laptop}) and (max-width: ${device.desktopS}) {
    padding: 63px 166px;
  }

  @media (min-width: ${device.desktopS}) and (max-width: ${device.desktop}) {
    padding: 78px 241px;
  }

  @media (min-width: ${device.desktop}) {
    padding: 78px 561px;
    max-width: 2560px;
  }
`;

const H2 = styled.h2``;

const Text = styled.p`
  margin-top: 28px;
  @media (min-width: ${device.tablet}) and (max-width: ${device.laptopM}) {
    margin-top: 30px;
  }

  @media (min-width: ${device.laptop}) and (max-width: ${device.desktopS}) {
    margin-top: 13px;
  }

  @media (min-width: ${device.desktopS}) and (max-width: ${device.desktop}) {
    margin-top: 53px;
  }

  @media (min-width: ${device.desktop}) {
    margin-top: 53px;
  }
`;

const HeaderModule = ({
	backgroundColor,
	headline,
	mainText,
	removeNegativeSpacingBelowModule,
}: IModuleHeaderFields) => {
	const renderQRCodeRow = (url, bgColor) => (
		<CenterContainer>
			<QRCode value={url} bgColor={bgColor} />
		</CenterContainer>
	);

	const pathname = usePathname();
	const isDomesticViolencePage = pathname === "/domestic-violence";

	// see if we are looking at a branch URL
	const [branchUrl, setBranchUrl] = useState<string | null>(null);

	useEffect(() => {
		if (window?.location?.pathname?.startsWith("/verify") && window?.branch) {
			window.branch.data((err, data) => {
				window.branch.link(
					{
						data: {
							felix_app_custom_data: data?.data_parsed?.felix_app_custom_data,
							felix_app_screen_name: data?.data_parsed?.felix_app_screen_name,
						},
					},
					(error, link) => {
						if (error) {
							return;
						}
						setBranchUrl(link);
					},
				);
			});
		}
	}, []);

	const options = {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node, children) => <Text>{children}</Text>,
			[BLOCKS.HR]: () => <hr />,
		},
	};
	const resetModuleMarginBottom = removeNegativeSpacingBelowModule;

	return (
		<Container
			data-testid={`header-module-${mainText || ""}-${headline || ""}-${pathname || ""}`
				.toString()
				.toLowerCase()
				.replace(/\s+/g, "-")}
			resetModuleMarginBottom={resetModuleMarginBottom}
			isDomesticViolencePage={isDomesticViolencePage}
		>
			<TextContainer backgroundColor={backgroundColor}>
				{headline && <H2>{headline}</H2>}
				{mainText && richTextHelper(mainText)}
				{branchUrl && renderQRCodeRow(branchUrl, getColours(backgroundColor))}
			</TextContainer>
		</Container>
	);
};

export default HeaderModule;
